import { Color, MeshStandardMaterial } from 'three';
import type { Material } from '@thrasher/shared';
import {
  asphaltMaps,
  brickMaps,
  concreteMaps,
  containerMaps,
  dirtMaps,
  graniteMaps,
  grassMaps,
  paverMaps,
  plankMaps,
  plasterMaps,
  plywoodMaps,
  rampMaps,
  tileMaps,
} from './textures.js';
import type { SurfaceMaps } from './textures.js';

function textured(
  maps: SurfaceMaps,
  options: { roughness?: number; metalness?: number; env?: number; normal?: number } = {},
): MeshStandardMaterial {
  const material = new MeshStandardMaterial({
    map: maps.map,
    normalMap: maps.normalMap,
    roughness: options.roughness ?? 1,
    metalness: options.metalness ?? 0,
    vertexColors: true,
    envMapIntensity: options.env ?? 0.55,
  });
  material.normalScale.setScalar(options.normal ?? 1);
  if (maps.roughnessMap) material.roughnessMap = maps.roughnessMap;
  return material;
}

function painted(
  color: string,
  roughness: number,
  metalness: number,
  env = 0.9,
): MeshStandardMaterial {
  return new MeshStandardMaterial({
    color: new Color(color),
    roughness,
    metalness,
    vertexColors: true,
    envMapIntensity: env,
  });
}

/** Builds one material per course surface type. Low quality skips normal maps. */
export function createCourseMaterials(): Record<Material, MeshStandardMaterial> {
  const concrete = concreteMaps();
  const container = {
    red: containerMaps([168, 52, 40], 201),
    blue: containerMaps([44, 86, 148], 202),
    orange: containerMaps([214, 120, 40], 203),
  };
  return {
    asphalt: textured(asphaltMaps()),
    concrete: textured(concrete),
    pavers: textured(paverMaps()),
    grass: textured(grassMaps(), { env: 0.25 }),
    dirt: textured(dirtMaps(), { env: 0.25 }),
    plaster: textured(plasterMaps(), { env: 0.7 }),
    tile: textured(tileMaps(), { env: 1.1 }),
    coping: painted('#e9e1d2', 0.55, 0, 0.6),
    ramp: textured(rampMaps(), { env: 0.8 }),
    rampSide: textured(plywoodMaps()),
    deck: textured(plankMaps()),
    metal: painted('#b9bec8', 0.3, 0.95, 1.2),
    railRed: painted('#d8352b', 0.32, 0.55),
    railYellow: painted('#f2c22e', 0.32, 0.55),
    granite: textured(graniteMaps(), { env: 0.8 }),
    brick: textured(brickMaps()),
    containerRed: textured(container.red, { env: 0.8 }),
    containerBlue: textured(container.blue, { env: 0.8 }),
    containerOrange: textured(container.orange, { env: 0.8 }),
    wood: textured(plankMaps([176, 132, 88], 131)),
    curb: textured(concreteMaps([214, 180, 60], 141)),
    paintedWall: textured(concrete),
  };
}
